<template>
  <div class="statuses-tab">
    <div class="status-pills">
      <div
        v-for="status in STATUSES"
        :key="status.name"
        class="status-pill"
        :class="status.cls"
      >
        <span class="pill-label">{{ status.name }}</span>
        <span class="pill-value">{{ fmtNum(statusCount(status.name)) }}</span>
      </div>
    </div>

    <div v-if="lossKpis" class="loss-row">
      <div class="loss-card loss-bad">
        <div class="loss-label">Штрафы за срывы (ТОП — 100% ставки без НДС, КА и прочие — 25%)</div>
        <div class="loss-value">{{ fmtMoney(kpis && kpis.breakdown_loss_total) }}</div>
      </div>
      <div class="loss-card">
        <div class="loss-label">Чистая комиссия (минус Фонд Кипиай)</div>
        <div class="loss-value">{{ fmtMoney(kpis && kpis.net_commission_total) }}</div>
      </div>
    </div>

    <div class="chart-block">
      <div class="chart-head">
        <div class="chart-title">Статусы рейсов по логистам</div>
        <label class="hide-zero">
          <input type="checkbox" v-model="hideZeroSeries" />
          Скрыть нулевые серии
        </label>
      </div>
      <VChart v-if="matrixRows.length" class="chart" :option="stackedOption" autoresize />
      <div v-else-if="isLoading" class="chart-empty">Загрузка…</div>
      <div v-else class="chart-empty">Нет данных по выбранным фильтрам</div>
    </div>

    <div class="table-scroll">
      <table class="stat-table" v-if="summaryRows.length">
        <thead>
          <tr>
            <th>Статус</th>
            <th class="num">Рейсов</th>
            <th class="num">Доля</th>
            <th class="num">Ср. маржа</th>
            <th class="num">Ср. комиссия</th>
            <th class="num">Ср. поиск, ч.</th>
            <th class="num">Штрафы срывов</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in summaryRows" :key="row.status_name">
            <td><span class="status-pill-sm" :class="statusClass(row.status_name)">{{ row.status_name }}</span></td>
            <td class="num strong">{{ fmtNum(row.trips) }}</td>
            <td class="num">{{ fmtPct(row.share_pct) }}</td>
            <td class="num">{{ fmtPct(row.avg_margin_pct) }}</td>
            <td class="num">{{ fmtMoney(row.avg_commission) }}</td>
            <td class="num">{{ fmtHoursNum(row.avg_search_sec) }}</td>
            <td class="num" :class="{ 'warn-cell': row.breakdown_loss_rub > 0 }">{{ fmtMoney(row.breakdown_loss_rub) }}</td>
          </tr>
          <tr class="total-row">
            <td>Итого</td>
            <td class="num strong">{{ fmtNum(totalTrips) }}</td>
            <td class="num">100%</td>
            <td class="num">—</td>
            <td class="num">—</td>
            <td class="num">—</td>
            <td class="num">{{ fmtMoney(totalLoss) }}</td>
          </tr>
        </tbody>
      </table>
      <div v-else-if="!isLoading" class="empty-table">Нет данных по выбранным фильтрам</div>
    </div>
  </div>
</template>

<script>
import { computed, ref } from 'vue';
import VChart from '@/components/charts/echarts';
import { fmtHoursNum, fmtMoney, fmtNum, fmtPct } from '../format';

const STATUSES = [
  { name: 'Открыт', cls: 'pill-open', color: '#3b82f6' },
  { name: 'Закрыт', cls: 'pill-closed', color: '#12b76a' },
  { name: 'Отменен', cls: 'pill-gray', color: '#98a2b3' },
  { name: 'Удален', cls: 'pill-gray', color: '#d0d5dd' },
  { name: 'Устарел', cls: 'pill-gray', color: '#667085' },
  { name: 'Срыв', cls: 'pill-red', color: '#f04438' },
  { name: 'Отказ', cls: 'pill-orange', color: '#f79009' },
];

const STATUS_FIELDS = {
  'Открыт': 'opened',
  'Закрыт': 'closed',
  'Отменен': 'canceled',
  'Удален': 'deleted',
  'Устарел': 'outdated',
  'Срыв': 'breakdown',
  'Отказ': 'refusal',
};

export default {
  name: 'StatusesTab',
  components: { VChart },
  props: {
    summaryRows: { type: Array, default: () => [] },
    matrixRows: { type: Array, default: () => [] },
    kpis: { type: Object, default: null },
    isLoading: { type: Boolean, default: false },
  },
  setup(props) {
    const hideZeroSeries = ref(false);

    const statusCount = (name) => {
      const row = props.summaryRows.find((r) => r.status_name === name);
      return row ? row.trips : 0;
    };

    const statusClass = (name) => {
      const status = STATUSES.find((s) => s.name === name);
      return status ? status.cls : 'pill-gray';
    };

    const stackedOption = computed(() => {
      const visibleStatuses = STATUSES.filter((status) => {
        if (!hideZeroSeries.value) return true;
        const field = STATUS_FIELDS[status.name];
        return props.matrixRows.some((row) => (Number(row[field]) || 0) > 0);
      });
      const labels = props.matrixRows.map((row) => row.logist || '—');
      return {
        grid: { left: 8, right: 16, top: 32, bottom: 8, containLabel: true },
        legend: {
          top: 0,
          type: 'scroll',
          textStyle: { fontSize: 11, color: '#667085' },
        },
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'shadow' },
          confine: true,
        },
        xAxis: {
          type: 'value',
          axisLabel: { formatter: (v) => fmtNum(v) },
        },
        yAxis: {
          type: 'category',
          inverse: true,
          data: labels,
          axisLabel: { width: 170, overflow: 'truncate' },
        },
        series: visibleStatuses.map((status) => ({
          name: status.name,
          type: 'bar',
          stack: 'statuses',
          barMaxWidth: 22,
          itemStyle: { color: status.color },
          data: props.matrixRows.map(
            (row) => Number(row[STATUS_FIELDS[status.name]]) || 0
          ),
        })),
      };
    });

    const totalTrips = computed(
      () => props.summaryRows.reduce((acc, row) => acc + (Number(row.trips) || 0), 0)
    );
    const totalLoss = computed(
      () =>
        props.summaryRows.reduce(
          (acc, row) => acc + (Number(row.breakdown_loss_rub) || 0),
          0
        )
    );

    const lossKpis = computed(
      () => props.kpis && (props.kpis.breakdown_loss_total != null || props.kpis.net_commission_total != null)
    );

    return {
      STATUSES,
      hideZeroSeries,
      statusCount,
      statusClass,
      stackedOption,
      totalTrips,
      totalLoss,
      lossKpis,
      fmtHoursNum,
      fmtMoney,
      fmtNum,
      fmtPct,
    };
  },
};
</script>

<style scoped>
.statuses-tab {
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
}

.status-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.status-pill {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  background: #fff;
  font-size: 13px;
}

.pill-label {
  color: #344054;
}

.pill-value {
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

.pill-open { border-color: #bfdbfe; background: #eff6ff; }
.pill-open .pill-value { color: #1d4ed8; }
.pill-closed { border-color: #bbf7d0; background: #f0fdf4; }
.pill-closed .pill-value { color: #15803d; }
.pill-gray { border-color: #e5e7eb; background: #f8fafc; }
.pill-gray .pill-value { color: #475467; }
.pill-red { border-color: #fecdd3; background: #fef3f2; }
.pill-red .pill-value { color: #b42318; }
.pill-orange { border-color: #fedf89; background: #fffaeb; }
.pill-orange .pill-value { color: #b54708; }

.loss-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 12px;
}

.loss-card {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 12px 16px;
  background: #fff;
}

.loss-bad {
  border-color: #fecdd3;
  background: #fef3f2;
}

.loss-label {
  font-size: 12px;
  color: #667085;
  margin-bottom: 4px;
}

.loss-value {
  font-size: 18px;
  font-weight: 700;
  color: #1d2939;
  font-variant-numeric: tabular-nums;
}

.loss-bad .loss-value {
  color: #b42318;
}

.chart-block {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
  padding: 14px;
}

.chart-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;
}

.chart-title {
  font-size: 14px;
  font-weight: 600;
  color: #344054;
}

.hide-zero {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #667085;
  cursor: pointer;
  white-space: nowrap;
}

.chart {
  width: 100%;
  height: 630px;
}

.chart-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  color: #98a2b3;
  font-size: 14px;
}

.table-scroll {
  overflow-x: auto;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
}

.stat-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.stat-table th {
  position: sticky;
  top: 0;
  background: #f8fafc;
  color: #667085;
  font-weight: 600;
  text-align: left;
  padding: 10px 12px;
  border-bottom: 1px solid #e5e7eb;
  white-space: nowrap;
}

.stat-table td {
  padding: 8px 12px;
  border-bottom: 1px solid #f2f4f7;
  color: #1d2939;
  white-space: nowrap;
}

.stat-table tbody tr:hover {
  background: #f8fafc;
}

.total-row td {
  background: #f8fafc;
  font-weight: 600;
  border-top: 1px solid #e5e7eb;
}

.num {
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.strong {
  font-weight: 600;
}

.warn-cell {
  color: #d92d20;
}

.status-pill-sm {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 500;
}

.status-pill-sm.pill-open { background: #eff6ff; color: #1d4ed8; }
.status-pill-sm.pill-closed { background: #f0fdf4; color: #15803d; }
.status-pill-sm.pill-gray { background: #f2f4f7; color: #475467; }
.status-pill-sm.pill-red { background: #fef3f2; color: #b42318; }
.status-pill-sm.pill-orange { background: #fffaeb; color: #b54708; }

.empty-table {
  padding: 40px;
  text-align: center;
  color: #98a2b3;
  font-size: 14px;
}
</style>
